import React, { useMemo, useState } from "react";
import { Button, Upload, Dropdown, Popconfirm, Modal, Row, Col } from "antd";
import type { MenuProps } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import UserService from "../../services/UserService";
import DocumentService from "../../services/DocumentService";
import { openNotification } from "../../utils";
import CustomerService from "../../services/CustomerService";

interface IdentityActionsProps {
  userId: string;
  identityDocumentUrl?: string | null;
  identityDocumentType?: string | null;           // "image" | "pdf" | "other"
  identityDocumentConfirmAt?: string | null;
  refreshUser: () => void; // parent'tan getCustomer çağırmak için
}

type DocType = "image" | "pdf" | "other";

const detectFileType = (url: string): DocType => {
  const lower = url.toLowerCase();
  if ([".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) => lower.includes(ext))) return "image";
  if (lower.includes(".pdf")) return "pdf";
  return "other";
};

const IdentityActions: React.FC<IdentityActionsProps> = ({
  userId,
  identityDocumentUrl,
  identityDocumentType,
  identityDocumentConfirmAt,
  refreshUser,
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const effectiveType: DocType | null = useMemo(() => {
    if (!identityDocumentUrl) return null;
    if (identityDocumentType === "image" || identityDocumentType === "pdf" || identityDocumentType === "other") {
      return identityDocumentType;
    }
    return detectFileType(identityDocumentUrl);
  }, [identityDocumentUrl, identityDocumentType]);

  // Upload
  const handleIdentityUpload = async (file: File) => {
    try {
      setUploading(true);
      const uploadResponse: any = await DocumentService.postDocument(file, file.name);
      const documentId: any = uploadResponse?.data?.id;
      if (!documentId) throw new Error("Belge yüklenemedi");

      const docRes: any = await DocumentService.getDocument(documentId);
      const url =
        docRes?.data?.preSignUrl ||
        docRes?.data?.url ||
        docRes?.data?.preSignedUrl;
      if (!url) throw new Error("Belge URL alınamadı");

      const type: DocType = detectFileType(url);

      await UserService.update(userId, {
        identityDocumentUrl: url,
        identityDocumentType: type,
        identityDocumentConfirmAt: null,
      });

      openNotification("success", "Başarılı", "Kimlik kartı yüklendi.");
      refreshUser();
    } catch (e) {
      console.error(e);
      openNotification("error", "Hata", "Kimlik kartı yüklenemedi.");
    } finally {
      setUploading(false);
    }
    return false;
  };

  // Sil
  const handleIdentityDelete = async () => {
    try {
      await UserService.update(userId, {
        identityDocumentUrl: null,
        identityDocumentType: null,
        identityDocumentConfirmAt: null,
      });
      openNotification("success", "Başarılı", "Kimlik kartı silindi.");
      refreshUser();
    } catch (e) {
      console.error(e);
      openNotification("error", "Hata", "Kimlik kartı silinemedi.");
    }
  };

  // Onayla
  const handleIdentityConfirm = async () => {
    try {
      await CustomerService.confirmIdentity(userId); // POST /api/v1/customers/{id}/identity-confirm
      openNotification("success", "Başarılı", "Kimlik kartı onaylandı.");
      refreshUser();
    } catch (e) {
      console.error(e);
      openNotification("error", "Hata", "Kimlik kartı onaylanamadı.");
    }
  };

  // Göster + İndir: modal aç
  const openPreview = () => {
    if (!identityDocumentUrl) return;
    setPreviewOpen(true);
  };

  // Modal içi "İndir"
  const handleDownload = () => {
    if (!identityDocumentUrl) return;
    const a = document.createElement("a");
    a.href = identityDocumentUrl;
    const guessedName = identityDocumentUrl.split("/").pop()?.split("?")[0] || "kimlik";
    a.setAttribute("download", guessedName);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Menü: Onayla & Sil (ikisi de Popconfirm ile, tüm satır tıklanabilir)
  const menuItems: MenuProps["items"] = [
    {
      key: "confirm",
      label: (
        <Popconfirm
          title="Kimlik Kartını onaylamak istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={(e) => {
            e?.stopPropagation?.();
            if (!identityDocumentConfirmAt) handleIdentityConfirm();
          }}
          onCancel={(e) => e?.stopPropagation?.()}
        >
          <span
            className="menu-action-full"
            onClick={(e) => e.stopPropagation()}
            aria-disabled={!!identityDocumentConfirmAt}
            style={identityDocumentConfirmAt ? { opacity: 0.5, pointerEvents: "none" } : undefined}
          >
            Onayla
          </span>
        </Popconfirm>
      ),
      disabled: !!identityDocumentConfirmAt,
    },
    {
      key: "delete",
      label: (
        <Popconfirm
          title="Kimlik kartını silmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          okButtonProps={{ danger: true }}
          onConfirm={(e) => {
            e?.stopPropagation?.();
            handleIdentityDelete();
          }}
          onCancel={(e) => e?.stopPropagation?.()}
        >
          <span
            className="menu-action-full"
            onClick={(e) => e.stopPropagation()}
          >
            Sil
          </span>
        </Popconfirm>
      ),
      danger: true,
    },
  ];

  // Menü click'te bir şey yapmıyoruz; Popconfirm içinden yönetiliyor.
  const onMenuClick: MenuProps["onClick"] = ({ domEvent }) => {
    domEvent.stopPropagation();
  };

  return identityDocumentUrl ? (
    <>
      <span className="mb-8px">Türkiye Cumhuriyeti Kimlik Kartı</span>

      <Dropdown.Button
        menu={{ items: menuItems, onClick: onMenuClick }}
        onClick={openPreview}
      >
        Görüntüle / İndir
      </Dropdown.Button>

      <Modal
        title="Kimlik Belgesi"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[]}
        width={900}
        bodyStyle={{ padding: 16 }}
      >
        {effectiveType === "image" ? (
          <div style={{ textAlign: "center" }}>
            <img
              src={identityDocumentUrl}
              alt="Kimlik"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <p>Bu dosya önizlenemiyor. Aşağıdan indirebilirsiniz.</p>
            <Button onClick={handleDownload}>İndir</Button>
          </div>
        )}
      </Modal>
    </>
  ) : (<>
    <Row gutter={[16,16]}>
      <Col span={24}>Türkiye Cumhuriyeti Kimlik Kartı</Col>
      <Col>
        <Upload
          showUploadList={false}
          beforeUpload={(file) => {
            handleIdentityUpload(file as any);
            return false;
          }}
          accept="image/*,application/pdf"
        >
          <Button loading={uploading} icon={<UploadOutlined />}>
            Kimlik Kartı Yükle
          </Button>
        </Upload>
      </Col>
    </Row>

  </>
  );
};

export default IdentityActions;
